import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';
import { usePulseSegments } from '@pulse/react';

import useElementInView from 'hooks/useElementInView';
import useHeaderHeight from 'hooks/useHeaderHeight';

import { useCategory } from 'templates/hooks';
import useSubCategoryDataStore from 'templates/stores/subCategoryDataStore';

import TemplatesCount from 'templates/components/TemplatesCount';
import categoryNameConstruct from 'utils/categoryNameConstruct';
import StickyFilters from './components/StickyFilters';

import useStyles from './styles';
import Content from './components/Content';
import ContentFallBack from './components/Content/FallBack';

const Filters = () => {
  const { category, subCategory } = useParams();
  const { isSearchPage } = useCategory();
  const { finalCategory } = categoryNameConstruct(category, subCategory);

  const contentRef = useRef<HTMLDivElement>(null);

  const { height: headerHeight } = useHeaderHeight();

  const { isElementInView } = useElementInView({ elementRef: contentRef, bottom: headerHeight });

  const classes = useStyles({ isElementInView });

  const { segments, loading: isSegmentsLoading } = usePulseSegments();

  const { fetchSubCategoryData, subCategories, isLoading } = useSubCategoryDataStore(state => ({
    fetchSubCategoryData: state.fetchSubCategoryData,
    subCategories: state.subCategories,
    isLoading: state.isLoading,
  }));

  useEffect(() => {
    if (isSegmentsLoading || isSearchPage) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      await fetchSubCategoryData(finalCategory, segments);
    })();
  }, [finalCategory, fetchSubCategoryData, isSearchPage, isSegmentsLoading, segments]);

  if (isLoading && !isSearchPage) {
    return <ContentFallBack />;
  }

  return (
    <div className={classes.root} data-pulse-section="filters">
      <div className={classes.wrapper} ref={contentRef}>
        {isElementInView && (
          <HorizontalContainer className={classes.contentContainer}>
            <div className={classes.contentWrapper}>
              <Content subCategories={subCategories} />
              <TemplatesCount />
            </div>
          </HorizontalContainer>
        )}
      </div>
      {!isElementInView && (
        <StickyFilters subCategories={subCategories} headerHeight={headerHeight} />
      )}
    </div>
  );
};

export default Filters;
